<template>
  <div class="invoice-recipient-info">
    <div class="invoice-recipient-info__header">
      <div class="invoice-recipient-info__icon">
        <img :src="ReceiptIcon" alt="Invoice" />
      </div>
      <h4 class="invoice-recipient-info__title">Faktura informasjon</h4>
    </div>
    <p class="invoice-recipient-info__description">
      Nedenfor finner du informasjon om hvor fakturaen skal sendes for denne
      avtalen.
    </p>
    <div class="invoice-recipient-info__details">
      <div
        v-for="(item, index) in recipientItems"
        :key="index"
        class="invoice-recipient-info__detail-item"
      >
        <span class="invoice-recipient-info__detail-label"
          >{{ item.label }}:</span
        >
        <span class="invoice-recipient-info__detail-value">{{
          item.value
        }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import ReceiptIcon from "@/assets/img/receipt.svg";

interface InvoiceRecipient {
  receivingOrganization?: {
    name?: string;
    orgNumber?: string;
  };
  email?: string;
  externalReference?: string;
}

const props = defineProps<{
  invoiceRecipient: InvoiceRecipient | null;
  referenceCode?: string;
}>();

const recipientItems = computed(() => {
  // If we have custom recipient data, show it
  if (props.invoiceRecipient) {
    const recipient = props.invoiceRecipient;
    const items = [];

    if (recipient.receivingOrganization?.name) {
      items.push({
        label: "Faktureringsfirma",
        value: recipient.receivingOrganization.name,
      });
    }

    if (recipient.receivingOrganization?.orgNumber) {
      items.push({
        label: "Org. nr.",
        value: recipient.receivingOrganization.orgNumber,
      });
    }

    if (recipient.email) {
      items.push({
        label: "Faktureringsmail",
        value: recipient.email,
      });
    }

    if (recipient.externalReference) {
      items.push({
        label: "Referanse",
        value: `#${recipient.externalReference}`,
      });
    }

    return items;
  }

  // Default behavior - show Really! AS details for scenarios 1 & 4
  return [
    {
      label: "Firmanavn",
      value: "Really! AS",
    },
    {
      label: "Org. nr.",
      value: "925401919",
    },
    {
      label: "Epost",
      value: "invoice@really.no",
    },
    {
      label: "Referanse",
      value: props.referenceCode ? `#${props.referenceCode}` : "#REF",
    },
  ];
});
</script>

<style lang="scss" scoped>
.invoice-recipient-info {
  background: #f0f7ff;
  border-radius: 8px;
  padding: 16px;

  &__header {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__icon {
    width: 32px;
    height: 32px;
  }

  &__title {
    color: #1d1d1d;
    font-size: 14px;
    font-weight: 600;
  }

  &__description {
    color: #1d1d1d;
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 16px;
  }

  &__details {
    background: rgba(255, 255, 255, 0.6);
    border-radius: 8px;
    padding: 12px;
  }

  &__detail-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__detail-label {
    color: #1d1d1d;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  &__detail-value {
    color: #1d1d1d;
    font-size: 14px;
    font-weight: 600;
  }
}
</style>
